import { useCallback } from "react";
import {
  AdvancedMarker,
  useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
import { lighten, rgba } from "polished";

import { type Marker } from "@googlemaps/markerclusterer";
import { Property } from "gql/generated.graphql";
import { useNavigate } from "react-router-dom";
import { IdxPreviousPageState } from "v2/pages/idx/idx-embedded-search-page";
import { MobilePropertyDetailsNavigation } from "../exchange-map-view";

type PropertyMarkerProps = {
  property: Property;
  idxFeedId?: string;
  branding?: { mainColor?: string; textColor?: string };
  onMarkerClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  setMobilePropertyNavigation?: React.Dispatch<
    React.SetStateAction<MobilePropertyDetailsNavigation | null>
  >;
  isSelected: boolean;
  setMarkerRef: (marker: Marker | null, key: string) => void;
  isHovered: boolean;
  isMobile: boolean;
  style?: React.CSSProperties;
  zIndex?: number;
  zoomLevel?: number;
  currentState?: IdxPreviousPageState;
};

export function PropertyMarker(props: PropertyMarkerProps) {
  const {
    onMarkerClick,
    isSelected,
    setMarkerRef,
    isHovered,
    isMobile,
    zoomLevel,
    currentState,
    setMobilePropertyNavigation,
    ...advancedMarkerProps
  } = props;
  const navigate = useNavigate();

  const [markerRef, marker] = useAdvancedMarkerRef();
  const property = props.property;

  const position = {
    lat: property.location.geoLocation.coordinates[1],
    lng: property.location.geoLocation.coordinates[0],
  };

  const mainColor = props.branding?.mainColor || "#0284c7";

  const navigateToPropertyDetails = useCallback(() => {
    navigate(`/exchange/embedded/${props.idxFeedId}/property/${property._id}`, {
      state: { ...currentState, mapState: { position, zoomLevel } },
    });
  }, [navigate, props.idxFeedId, property._id, zoomLevel, currentState]);

  const handleMarkerClick = useCallback(() => {
    if (isMobile) {
      if (marker) {
        onMarkerClick();
        if (setMobilePropertyNavigation) {
          setMobilePropertyNavigation({ navigateToPropertyDetails });
        }
      }
    } else {
      navigateToPropertyDetails();
    }
  }, [marker, onMarkerClick, navigateToPropertyDetails]);

  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) => {
      setMarkerRef(marker, property._id);
      markerRef(marker);
    },
    [setMarkerRef, property._id]
  );

  return (
    <>
      <AdvancedMarker
        position={position}
        clickable
        onClick={handleMarkerClick}
        ref={ref}
        {...advancedMarkerProps}
      >
        <div className="flex flex-col items-center">
          {(isHovered || isSelected) && (
            <div
              className="absolute rounded-full"
              style={{
                width: "46px",
                height: "46px",
                top: "-3px",
                left: "-3px",
                backgroundColor: rgba(lighten(0.3, mainColor), 0.7),
                zIndex: -1,
              }}
            ></div>
          )}
          <div
            className={`flex items-center justify-center border-4 size-10 rounded-full `}
            style={{
              backgroundColor: mainColor,
              top: 4,
              overflow: "hidden",
              transform: "scale(0.5)",
            }}
          ></div>
        </div>
      </AdvancedMarker>
    </>
  );
}
